// =============================================================================
// Investor Investments Page
// =============================================================================

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  BanknotesIcon,
  MapPinIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import { Card, DealImage } from '../../components/ui';
import { investorService, type InvestmentListItem } from '../../services/investor.service';

// =============================================================================
// Component
// =============================================================================

export function InvestmentsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [investments, setInvestments] = useState<InvestmentListItem[]>([]);
  const [stats, setStats] = useState({
    totalInvested: 0,
    totalDistributed: 0,
    avgCashYield: 0,
    activeDeals: 0,
  });

  useEffect(() => {
    loadInvestments();
  }, []);

  const loadInvestments = async () => {
    setIsLoading(true);
    try {
      // Fetch investments and dashboard stats in parallel
      const [investmentsList, dashboardStats] = await Promise.all([
        investorService.getInvestments(),
        investorService.getDashboard(),
      ]);

      setInvestments(investmentsList);

      // Calculate stats
      const totalInvested = investmentsList.reduce((sum, inv) => sum + inv.investmentAmount, 0);
      const totalDistributed = investmentsList.reduce((sum, inv) => sum + inv.totalDistributed, 0);

      // Count unique deals
      const uniqueDeals = new Set(investmentsList.map(inv => inv.deal.id));

      setStats({
        totalInvested,
        totalDistributed,
        avgCashYield: dashboardStats.avgCashYield || 0,
        activeDeals: uniqueDeals.size,
      });
    } catch (error) {
      console.error('Failed to load investments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  const getPropertyTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      MULTIFAMILY: 'Multifamily',
      RETAIL: 'Retail',
      OFFICE: 'Office',
      INDUSTRIAL: 'Industrial',
      MIXED_USE: 'Mixed Use',
      HOTEL: 'Hotel',
      SELF_STORAGE: 'Self Storage',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  const getPropertyTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      MULTIFAMILY: 'bg-blue-100 text-blue-800',
      RETAIL: 'bg-purple-100 text-purple-800',
      OFFICE: 'bg-yellow-100 text-yellow-800',
      INDUSTRIAL: 'bg-gray-100 text-gray-800',
      MIXED_USE: 'bg-green-100 text-green-800',
      HOTEL: 'bg-pink-100 text-pink-800',
      SELF_STORAGE: 'bg-orange-100 text-orange-800',
      OTHER: 'bg-gray-100 text-gray-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  // Calculate annualized cash yield for a single investment
  const calculateAnnualizedYield = (investment: InvestmentListItem) => {
    if (!investment.investmentAmount || investment.investmentAmount === 0) return 0;
    if (!investment.totalDistributed || investment.totalDistributed === 0) return 0;

    // Calculate years held
    const acquisitionDate = investment.deal.acquisitionDate
      ? new Date(investment.deal.acquisitionDate)
      : new Date();
    const now = new Date();
    const yearsHeld = (now.getTime() - acquisitionDate.getTime()) / (1000 * 60 * 60 * 24 * 365);

    if (yearsHeld <= 0) return 0;

    // Annualized yield = (total distributed / investment amount) / years held * 100
    const totalReturn = investment.totalDistributed / investment.investmentAmount;
    const annualizedYield = (totalReturn / yearsHeld) * 100;

    return annualizedYield;
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-xl" />
          ))}
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-40 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Investments</h1>
        <p className="mt-1 text-sm text-gray-500">
          Overview of your real estate investment portfolio
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <BanknotesIcon className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Invested</p>
              <p className="text-lg font-bold text-gray-900">
                {formatCurrency(stats.totalInvested)}
              </p>
            </div>
          </div>
        </Card>

        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <BanknotesIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Distributed</p>
              <p className="text-lg font-bold text-green-600">
                {formatCurrency(stats.totalDistributed)}
              </p>
            </div>
          </div>
        </Card>

        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ArrowTrendingUpIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Avg. Annualized Cash Yield</p>
              <p className="text-lg font-bold text-gray-900">
                {stats.avgCashYield.toFixed(1)}%
              </p>
            </div>
          </div>
        </Card>

        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <BuildingOfficeIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Active Investments</p>
              <p className="text-lg font-bold text-gray-900">{stats.activeDeals}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Investments List */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Properties</h2>
        <div className="space-y-4">
          {investments.map((investment) => (
            <Card key={`${investment.deal.id}-${investment.profile.id}`} hover padding="md">
              <Link to={`/investments/${investment.deal.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Property Info */}
                  <div className="flex items-start gap-4">
                    {/* Property Image - Using proxy to bypass browser restrictions */}
                    <div className="hidden sm:block w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                      <DealImage
                        dealId={investment.deal.id}
                        dealName={investment.deal.name}
                        imageUrl={investment.deal.imageUrl}
                        className="w-full h-full object-cover"
                        iconClassName="h-8 w-8 text-primary-600"
                        fallbackClassName="h-full w-full flex items-center justify-center bg-primary-100"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {investment.deal.name}
                        </h3>
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPropertyTypeColor(
                            investment.deal.propertyType
                          )}`}
                        >
                          {getPropertyTypeLabel(investment.deal.propertyType)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                        <MapPinIcon className="h-4 w-4" />
                        {investment.deal.address}, {investment.deal.city},{' '}
                        {investment.deal.state} {investment.deal.zipCode}
                      </div>
                      {investment.profile.entityName && (
                        <p className="text-sm text-primary-600 mt-1">
                          via {investment.profile.entityName}
                        </p>
                      )}
                      {!investment.profile.entityName && investment.profile.legalName && (
                        <p className="text-sm text-gray-500 mt-1">
                          Individual Account
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        Acquired: {formatDate(investment.deal.acquisitionDate)}
                      </p>
                    </div>
                  </div>

                  {/* Investment Details */}
                  <div className="grid grid-cols-3 gap-4 lg:gap-8 text-right">
                    <div>
                      <p className="text-xs text-gray-500">Cash Yield</p>
                      <p className="text-sm font-semibold text-blue-600">
                        {calculateAnnualizedYield(investment).toFixed(1)}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Ownership</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {investment.ownershipPercentage.toFixed(2)}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Distributed</p>
                      <p className="text-sm font-semibold text-green-600">
                        {formatCurrency(investment.totalDistributed)}
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            </Card>
          ))}
        </div>
      </div>

      {/* Empty State */}
      {investments.length === 0 && (
        <Card padding="lg">
          <div className="text-center py-12">
            <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No Investments Yet</h3>
            <p className="mt-2 text-sm text-gray-500">
              Your investments will appear here once they are recorded.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

export default InvestmentsPage;
